/**
 * Which insurers each centre is in-network with, so the insurance band shows
 * that centre's real payers instead of one national set.
 *
 * SOURCE: views/paymentOptionsDetails/insurancePlansByState, the list that
 * drives the payment options page. It is maintained and business-approved: it
 * was last revised from a list supplied by Sarah Aloisi, and Alliance Health
 * and AmeriHealth were added to it in August 2026.
 *
 * The lists below were checked against that file and signed off in August
 * 2026. Treat any change to either as needing the same sign-off, since these
 * are coverage claims.
 *
 * It deliberately does NOT match Contentful's nine "In-network insurance
 * plans" images, which are years out of date. Those still list Kaiser, removed
 * from the site in December 2025, along with Magellan, ComPsych, Managed
 * Health Network, and a long tail of Texas PPO networks the approved list
 * dropped. Treat the images as historical, not as a source.
 *
 * Keep this in step with insurancePlansByState by hand. They are not shared
 * because that page renders plan names as text, including entries like
 * "Self-pay" and coverage qualifiers, while the band needs logos.
 *
 * HOW A LOGO APPEARS, in priority order:
 *   1. A Contentful asset titled exactly the payer's `logo` value. Preferred,
 *      because it is editable without a deploy.
 *   2. The payer's `image`, a file in static/images/insurers. These exist so
 *      the redesign can be previewed before anything is uploaded, and should
 *      be deleted once Contentful holds the same artwork.
 * A payer with neither renders as its name instead. Dropping an approved
 * payer would understate coverage, so the band never omits one.
 */

import { clinicKey } from "./clinicOverrides"

/** Prefix every insurer logo asset title shares, so one query can fetch them. */
export const LOGO_PREFIX = "Insurer Logo - "

const payer = (label, image, logoName) => ({
  image,
  label,
  logo: `${LOGO_PREFIX}${logoName || label}`,
})

/**
 * Every payer named by the approved list. `label` is what a family would
 * recognise, `image` the file in static/images/insurers, `logo` the Contentful
 * asset title to upload against.
 *
 * Where the list names two brands together ("Cigna/Evernorth Behavioral
 * Healthcare") they are separate payers here, because they are separate logos.
 */
const PAYERS = {
  aetna: payer("Aetna", "aetna"),
  allianceHealth: payer("Alliance Health", "alliance-health"),
  ameriHealth: payer("AmeriHealth", "amerihealth"),
  // Two different Anthem marks. California uses Blue Cross alone, because Blue
  // Shield of California is a separate company there. Not interchangeable.
  anthemBcbs: payer("Anthem Blue Cross Blue Shield", "anthem-bcbs"),
  anthemBlueCross: payer("Anthem Blue Cross", "anthem-blue-cross"),
  bcbs: payer("Blue Cross Blue Shield", "bcbs"),
  beHealthy: payer("BeHealthy Partnership", "behealthy"),
  blueShieldCa: payer("Blue Shield of California", "blue-shield-ca"),
  cigna: payer("Cigna", "cigna"),
  connecticare: payer("ConnectiCare", "connecticare"),
  easterseals: payer("Easterseals", "easterseals"),
  evernorth: payer("Evernorth Behavioral Health", "evernorth"),
  fallon: payer("Fallon Health", "fallon"),
  firstHealth: payer("First Health", "first-health"),
  harvardPilgrim: payer("Harvard Pilgrim Health Plan", "harvard-pilgrim"),
  healthnet: payer("Health Net", "healthnet"),
  horizonBcbs: payer("Horizon Blue Cross Blue Shield", "horizon-bcbs"),
  humana: payer("Humana", "humana"),
  imagineHealth: payer("Imagine Health", "imagine-health"),
  massGeneralBrigham: payer(
    "Mass General Brigham Health Plan",
    "mass-general-brigham"
  ),
  // The supplied mark puts the wordmark on top of a light-grey state
  // silhouette. Both are opaque, so knocking them to white merged them into
  // one unreadable shape. The silhouette has been removed and only the
  // wordmark kept.
  massHealth: payer("MassHealth", "masshealth"),
  mercyCare: payer("Mercy Care", "mercy-care"),
  optum: payer("Optum Behavioral Health", "optum"),
  scottWhite: payer("Scott & White Health Plan", "scott-white"),
  sfHealthPlan: payer("San Francisco Health Plan", "sf-health-plan"),
  superior: payer("Superior/Ambetter", "superior"),
  tufts: payer("Tufts Health Plan", "tufts"),
  // A separate plan from Tufts Health Plan, confirmed August 2026, so it keeps
  // its own line. No artwork yet; published sources all redirect to the Tufts
  // Health Plan or Point32Health marks, so this one needs art from the plan.
  // Until then it renders as its name rather than being dropped.
  tuftsPublic: payer("Tufts Health Public Plans", null),
  unitedHealthcare: payer("United Healthcare", "united-healthcare"),
  // The current post-2023 mark. Supplied on an opaque white background, which
  // the filter would have turned into a solid white block, so the background
  // and the letter counters have been knocked out to transparent.
  wellpoint: payer("Wellpoint", "wellpoint"),
  wellsense: payer("WellSense Health Plan", "wellsense"),
}

/**
 * The regions the approved list defines, named as it names them.
 *
 * `payers` are in-network for all services. `behavioralOnly` are in-network
 * for behavioral health only, a qualifier the list carries and that the band
 * must not flatten away, since doing so would overstate coverage.
 */
const REGIONS = {
  arizona: {
    payers: [
      "aetna",
      "bcbs",
      "cigna",
      "evernorth",
      "mercyCare",
      "unitedHealthcare",
      "optum",
    ],
  },
  california: {
    payers: [
      "aetna",
      "anthemBlueCross",
      "blueShieldCa",
      "cigna",
      "evernorth",
      "easterseals",
      "healthnet",
      "sfHealthPlan",
      "unitedHealthcare",
      "optum",
    ],
  },
  // HUSKY Care (Medicaid) was removed at Jensen's instruction, September 2026,
  // and is not to be added back. It is dropped from the payment options page in
  // the same change, so the two lists still agree.
  connecticut: {
    payers: [
      "aetna",
      "anthemBcbs",
      "cigna",
      "evernorth",
      "connecticare",
      "humana",
      "unitedHealthcare",
      "optum",
    ],
  },
  illinois: {
    payers: [
      "aetna",
      "bcbs",
      "cigna",
      "evernorth",
      "imagineHealth",
      "unitedHealthcare",
      "optum",
    ],
  },
  massNorth: {
    payers: [
      "aetna",
      "bcbs",
      "cigna",
      "evernorth",
      "harvardPilgrim",
      "tufts",
      "tuftsPublic",
      "unitedHealthcare",
      "optum",
      "wellpoint",
    ],
  },
  massSouthAndWest: {
    behavioralOnly: ["beHealthy"],
    payers: [
      "aetna",
      "bcbs",
      "wellsense",
      "cigna",
      "evernorth",
      "fallon",
      "harvardPilgrim",
      "massGeneralBrigham",
      "massHealth",
      "tufts",
      "tuftsPublic",
      "unitedHealthcare",
      "optum",
      "wellpoint",
    ],
  },
  newJersey: {
    behavioralOnly: ["optum"],
    payers: ["aetna", "ameriHealth", "cigna", "evernorth", "horizonBcbs"],
  },
  northCarolina: {
    behavioralOnly: ["optum"],
    payers: ["aetna", "allianceHealth", "bcbs", "cigna", "evernorth"],
  },
  texas: {
    behavioralOnly: ["scottWhite"],
    payers: [
      "aetna",
      "bcbs",
      "cigna",
      "evernorth",
      "firstHealth",
      "humana",
      "imagineHealth",
      "superior",
      "unitedHealthcare",
      "optum",
    ],
  },
}

/**
 * Which region each centre belongs to. The approved list splits Massachusetts
 * by centre, so those two groups are mapped centre by centre; everywhere else
 * is by state.
 *
 * Ridgefield and West Hartford are closed and exist only in dev, so they are
 * left out.
 */
const CLINIC_REGIONS = {
  scottsdale: "arizona",
  burlington: "massNorth",
  carlsbad: "california",
  concord: "california",
  glendale: "california",
  "inland-empire": "california",
  irvine: "california",
  "laguna-niguel": "california",
  marin: "california",
  "san-diego": "california",
  torrance: "california",
  "westlake-village": "california",
  cary: "northCarolina",
  charlotte: "northCarolina",
  trumbull: "connecticut",
  "east-mesa": "arizona",
  "glendale-west-phoenix": "arizona",
  westchester: "illinois",
  braintree: "massNorth",
  danvers: "massNorth",
  dedham: "massSouthAndWest",
  shrewsbury: "massSouthAndWest",
  "west-springfield": "massSouthAndWest",
  naperville: "illinois",
  warren: "newJersey",
  tempe: "arizona",
  plano: "texas",
  "the-woodlands": "texas",
}

/** Centres with no region mapped, so the gap can be reported, not hidden. */
export const CLINICS_WITHOUT_INSURERS = slugs =>
  (slugs || []).filter(slug => !CLINIC_REGIONS[clinicKey(slug)])

/**
 * The insurers for a centre, keeping the full-coverage and behavioral-only
 * split. Returns null where no region is mapped, so the band can fall back.
 */
export const insurersFor = slug => {
  const region = REGIONS[CLINIC_REGIONS[clinicKey(slug)]]
  if (!region) return null

  const expand = ids => (ids || []).map(id => PAYERS[id]).filter(Boolean)

  return {
    allServices: expand(region.payers),
    behavioralOnly: expand(region.behavioralOnly),
  }
}

/** "A, B, and C". One item is itself, two are joined with "and". */
const joinNames = names =>
  names.length < 3
    ? names.join(" and ")
    : `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`

/**
 * The centre's payers written out as prose, for the FAQ answer.
 *
 * Reads the same lists as the logo band on the same page, so the two can never
 * disagree. The behavioral-health-only split is kept rather than merged,
 * because merging it would claim full coverage the payer does not provide.
 *
 * Returns null where no region is mapped, so the question can be left off that
 * centre's page instead of answered with an empty list.
 */
export const insurerProse = slug => {
  const insurers = insurersFor(slug)
  if (!insurers?.allServices?.length) return null

  const behavioral = insurers.behavioralOnly.map(item => item.label)

  return {
    allServices: joinNames(insurers.allServices.map(item => item.label)),
    behavioralOnly: behavioral.length
      ? ` We are also in-network with ${joinNames(
          behavioral
        )} for behavioral health services only.`
      : "",
  }
}

/**
 * The centre's payers as one line, for the `paymentAccepted` property of its
 * MedicalClinic schema.
 *
 * Search engines and language models cannot read a logo, so the list has to
 * exist as text somewhere machine-readable as well as on the page. Behavioral
 * health only payers keep the qualifier, and self-pay is named because
 * paymentAccepted is about how a family can pay, not only who is in network.
 */
export const paymentAccepted = slug => {
  const insurers = insurersFor(slug)
  if (!insurers?.allServices?.length) return null

  return [
    ...insurers.allServices.map(item => item.label),
    ...insurers.behavioralOnly.map(
      item => `${item.label} (behavioral health only)`
    ),
    "Self-pay",
  ].join(", ")
}

/** Every logo asset title the site could use, for the upload checklist. */
export const ALL_LOGO_TITLES = [
  ...new Set(Object.values(PAYERS).map(item => item.logo)),
].sort()

export default PAYERS
